import axios from "axios";
import toast from "react-hot-toast";
import { ACCESS_TOKEN_KEY, API_BASE_URL } from "./constants";

const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

/**
 * Attach the bearer token to every request.
 *
 * Read from storage per-request rather than relying on `defaults`, so a token
 * refreshed in another tab (or before this module's defaults were set) is
 * still picked up.
 */
instance.interceptors.request.use(
  (config: any) => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Blank strings are sent as null so the API's nullable fields clear properly.
 * FormData and arrays are left alone — rewriting their entries would corrupt
 * file uploads and list payloads.
 */
instance.interceptors.request.use(
  (config: any) => {
    const data = config.data;
    const isPlainObject =
      data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      !(typeof FormData !== "undefined" && data instanceof FormData);

    if (isPlainObject) {
      Object.keys(data).forEach((key) => {
        if (data[key] === "" || data[key] === undefined) {
          data[key] = null;
        }
      });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Surface server and permission errors.
 *
 * 401 is deliberately NOT handled here. JWTAuthContext installs its own
 * response interceptor that refreshes the token and replays the request;
 * redirecting to /login from this interceptor ran first and tore the page down
 * before that refresh could complete, which produced a 401 → refresh → reload
 * loop on every page.
 */
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (!status) {
      // No response at all — network failure, CORS, or a cancelled request.
      return Promise.reject(error);
    }

    if (status >= 500) {
      toast.error(
        error.response?.data?.message ||
          error.response?.statusText ||
          "Something went wrong on the server"
      );
    }

    if (status === 403) {
      toast.error(
        error.response?.data?.detail ||
          "You do not have permission to do that"
      );
    }

    return Promise.reject(error);
  }
);

export default instance;
